// The emoji game — ports app/(privado)/jogos/emoji/page.tsx.

import { useState } from "react";
import { Link } from "react-router";
import { hintPenaltyMs, isWordResolved, timedRoundPoints } from "@historia/shared/validation";
import { emojiRoundResults, wordTime } from "@historia/shared/emoji";
import type { FinishedRound as RondaTerminada } from "@historia/shared/contracts";
import { VoltarAosJogos } from "@/components/jogos/BarraDeJogo";
import { NovaRonda } from "@/components/jogos/NovaRonda";
import { RondaAtiva } from "@/components/jogos/RondaAtiva";
import { EcraPalpite } from "@/components/jogos/Adivinhar";
import { useModoDeJogo } from "@/components/ui/EcraDeJogo";
import { formatarTempo } from "@/components/jogos/RondaCronometrada";
import { Consulta } from "@/components/ui/EstadoConsulta";
import { useTitle } from "@/components/ui/useTitle";
import { useActiveRound, useFinishedRounds } from "@/api/queries";
import { useSession } from "@/api/session";
import { useOutroNome } from "@/components/jogos/useOutroNome";

export function JogosEmoji() {
  useTitle("Jogo dos emojis");
  const query = useActiveRound();
  const { user } = useSession();

  // Qual palavra está a ser adivinhada, se alguma. Vive aqui e não dentro da
  // lista porque adivinhar deixou de ser um pop-up por cima da página: é o ecrã
  // todo, e é esta página que decide o que se vê.
  const [aAdivinhar, setAAdivinhar] = useState<string | null>(null);

  const palavras = query.data?.words ?? [];
  const aberta = palavras.find((p) => p.id === aAdivinhar) ?? null;

  // Enquanto se adivinha, a app cala-se — como nos tabuleiros.
  useModoDeJogo(aberta !== null);

  if (aberta && user) {
    return (
      <main>
        <EcraPalpite
          palavra={aberta}
          // A pergunta dos outros jogos, feita nos termos deste: a MINHA palavra
          // já foi resolvida? Se sim, ela já jogou e sair agora é desistir.
          elaJaJogou={palavras
            .filter((p) => p.autor_email === user.email)
            .every((p) => isWordResolved(p))}
          aoFechar={() => setAAdivinhar(null)}
        />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:px-5">
      <header className="mb-6">
        <VoltarAosJogos />
        <h1 className="font-titulo text-3xl text-tinta-800 sm:text-4xl">
          Jogo dos emojis
        </h1>
        <p className="mt-1 text-sm text-tinta-600">
          Cada um põe uma palavra em emojis; depois trocam e tentam adivinhar,
          contra o relógio. Cada pista descobre uma letra e soma{" "}
          {Math.round(hintPenaltyMs("emoji") / 1000)}s. Ganha quem for mais
          rápido.
        </p>
      </header>

      <Consulta query={query} linhas={2}>
        {(ativa) =>
          !user ? (
            <p className="text-tinta-600">Sessão inválida. Volta a entrar.</p>
          ) : ativa ? (
            // The API already masked the other person's unresolved words, so
            // the answers simply aren't in this payload.
            <RondaAtiva
              rondaId={ativa.round.id}
              email={user.email}
              palavras={ativa.words}
              aoAbrir={setAAdivinhar}
            />
          ) : (
            <SemRonda email={user.email} />
          )
        }
      </Consulta>

      <section className="mt-10">
        <h2 className="mb-3 font-titulo text-xl text-tinta-800">
          Como se joga
        </h2>
        <ul className="flex flex-col gap-1.5 rounded-2xl border border-ouro/20 bg-creme-50 px-5 py-4 text-sm text-tinta-700">
          <li>
            Cada um escolhe um <strong>tema</strong> e uma{" "}
            <strong>palavra</strong>, e desenha essa palavra com emojis. Uma
            palavra cada, por ronda.
          </li>
          <li>Uma palavra nunca se repete — nem de uma ronda para a outra.</li>
          <li>
            Depois trocam: cada uma tenta adivinhar a palavra da outra. O
            relógio arranca quando abres a palavra.
          </li>
          <li>
            A resposta aparece em <strong>traços</strong>, um por letra — os
            espaços e os traços de união vêem-se desde o início, para se
            perceber a forma.
          </li>
          <li>
            <strong>Tentativas à vontade.</strong> Enganares-te custa segundos,
            não uma vida.
          </li>
          <li>
            Uma <strong>pista</strong> descobre a letra seguinte e soma{" "}
            {Math.round(hintPenaltyMs("emoji") / 1000)}s. Nunca descobre a
            última: a palavra é sempre tua.
          </li>
          <li>
            Acentos, maiúsculas, espaços e pontuação <strong>nunca</strong>{" "}
            decidem nada — "coracao" serve para "CORAÇÃO". As letras é que têm
            de estar certas.
          </li>
          <li>
            Ganha a ronda quem somar <strong>menos tempo</strong>, contando as
            pistas.
          </li>
        </ul>
      </section>

      <div className="mt-8 border-t border-ouro/15 pt-6 text-center">
        <Link
          to="/jogos/emoji/historico"
          className="inline-block rounded-full border border-ouro/40 px-5 py-2.5 text-sm font-semibold text-ouro-600 transition-colors hover:bg-ouro/10"
        >
          📜 Histórico de rondas
        </Link>
      </div>
    </main>
  );
}

/**
 * Sem ronda a decorrer — mas primeiro, como acabou a última.
 *
 * Acertar a última palavra fecha a ronda, e fechar a ronda faz desaparecer a
 * ronda do ecrã: o cartão com o "Acertaste!" ia-se embora no mesmo instante em
 * que se ganhava. Era o pior sítio possível para não haver nada — o momento
 * mais bonito do jogo dava um ecrã vazio a dizer "não há nenhuma ronda".
 *
 * Agora o resultado fica aqui à espera: os dois tempos, quem ganhou, e o botão
 * para começar outra.
 */
function SemRonda({ email }: { email: string }) {
  const terminadas = useFinishedRounds();
  const ultima = terminadas.data?.[0] ?? null;

  return (
    <div className="flex flex-col gap-4">
      {ultima && <ResultadoRondaEmoji ronda={ultima} email={email} />}

      <div className="rounded-2xl border border-ouro/20 bg-creme-50 p-6 text-center">
        <p className="mb-4 text-tinta-700">
          {ultima
            ? "Prontas para outra?"
            : "Não há nenhuma ronda a decorrer. Comecem uma nova!"}
        </p>
        <NovaRonda />
      </div>
    </div>
  );
}

/** Como acabou uma ronda: o teu tempo, o dela, e os pontos. */
function ResultadoRondaEmoji({
  ronda,
  email,
}: {
  ronda: RondaTerminada;
  email: string;
}) {
  const palavras = ronda.words;
  const outro = useOutroNome();

  const pontos = timedRoundPoints(
    emojiRoundResults(
      palavras.map((p) => ({
        autor_email: p.autor_email,
        tempo_ms: p.tempo_ms,
        pistas: p.pistas,
        acertou_na: p.acertou_na,
        desistiu: p.desistiu,
      })),
    ),
    "emoji",
  );

  // Rondas de antes do cronómetro não pontuam assim — não há nada a mostrar.
  if (pontos.length === 0) return null;

  // O teu tempo é o das palavras que a outra pessoa escreveu.
  const tempoDe = (quem: string) =>
    palavras
      .filter((p) => p.autor_email !== quem)
      .reduce((t, p) => t + wordTime(p), 0);

  const meus = pontos.find((p) => p.email === email);
  const dela = pontos.find((p) => p.email !== email);
  const ganhei = meus && dela ? tempoDe(email) < tempoDe(dela.email) : false;
  const empate = meus && dela ? tempoDe(email) === tempoDe(dela.email) : false;

  return (
    <div className="rounded-2xl border border-ouro/30 bg-creme-50 p-5 text-center">
      <p className="text-sm text-tinta-600">A última ronda</p>
      <p className="mt-1 font-titulo text-2xl text-ouro-600">
        {empate
          ? "Empate — ao segundo!"
          : ganhei
            ? "Ganhaste esta ronda! 💛"
            : `Desta vez foi de ${outro}.`}
      </p>

      <div className="mt-3 flex items-center justify-center gap-6 text-sm">
        <span className="text-tinta-800">
          Tu{" "}
          <strong className="tabular-nums">
            {formatarTempo(tempoDe(email))}
          </strong>
          {meus && meus.points > 0 && (
            <span className="ml-1 font-semibold text-ouro-600">
              +{meus.points}
            </span>
          )}
        </span>
        {dela && (
          <span className="text-tinta-600">
            {outro}{" "}
            <strong className="tabular-nums">
              {formatarTempo(tempoDe(dela.email))}
            </strong>
            {dela.points > 0 && (
              <span className="ml-1 font-semibold text-ouro-600">
                +{dela.points}
              </span>
            )}
          </span>
        )}
      </div>

      <ul className="mt-4 flex flex-col gap-1 border-t border-ouro/10 pt-3 text-sm">
        {palavras.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-center gap-2 text-tinta-700"
          >
            <span aria-hidden>{p.emojis}</span>
            <span className="font-semibold text-tinta-900">
              {p.palavra ?? "—"}
            </span>
            {p.tempo_ms !== null && (
              <span className="text-xs text-tinta-500">
                {formatarTempo(wordTime(p))}
                {p.pistas > 0 &&
                  ` · ${p.pistas} ${p.pistas === 1 ? "pista" : "pistas"}`}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
